import { Component, Input, Output, EventEmitter, OnInit, OnDestroy, ViewChild, ElementRef, AfterViewChecked } from '@angular/core';
import { Subscription, interval, of } from 'rxjs';
import { switchMap, catchError } from 'rxjs/operators';
import { UserChatHistoryApiResponse, UserChatHistoryDto } from 'src/app/models/agent-lead-notification.model';
import { AgentLeadNotificationService } from 'src/app/services/agent-lead-notification.service';
import { MessageServiceService } from 'src/app/services/message-service.service';

interface SentMessage {
  text: string;
  time: Date;
}

interface TimelineMessage {
  role: string;
  message_text: string;
  options: Array<{ label: string }>;
  created_on_date: Date;
  isUser: boolean;
}

const CHAT_POLL_MS = 4000;

@Component({
  selector: 'app-agent-lead-chat-detail',
  templateUrl: './agent-lead-chat-detail.component.html',
  styleUrls: ['./agent-lead-chat-detail.component.scss']
})
export class AgentLeadChatDetailComponent implements OnInit, OnDestroy, AfterViewChecked {
  @Input() chatData!: UserChatHistoryApiResponse;
  @Input() agentId = 0;
  @Input() isReadOnly = false;
  @Output() close = new EventEmitter<void>();
  @ViewChild('chatBody', { static: false }) chatBodyRef: ElementRef | null = null;

  newMessage = '';
  isSending = false;
  isExiting = false;
  sentMessages: SentMessage[] = [];
  messages: UserChatHistoryDto[] = [];
  timelineMessages: TimelineMessage[] = [];

  private pollSubscription: Subscription | null = null;
  private shouldScroll = true;

  constructor(
    private agentLeadNotificationService: AgentLeadNotificationService,
    private messageService: MessageServiceService
  ) {}

  ngOnInit(): void {
    document.body.style.overflow = 'hidden';
    this.messages = (this.chatData && this.chatData.messages) ? this.chatData.messages : [];
    this.rebuildTimeline();
    // If isReadOnly wasn't set by parent, derive it from the chat response itself
    if (!this.isReadOnly && this.chatData && this.chatData.isAgentExit === 1) {
      this.isReadOnly = true;
    }
    this.startPolling();
  }

  ngOnDestroy(): void {
    document.body.style.overflow = '';
    if (this.pollSubscription) {
      this.pollSubscription.unsubscribe();
    }
  }

  ngAfterViewChecked(): void {
    if (this.shouldScroll) {
      this.scrollToBottom();
      this.shouldScroll = false;
    }
  }

  private startPolling(): void {
    this.pollSubscription = interval(CHAT_POLL_MS)
      .pipe(
        switchMap(() =>
          this.agentLeadNotificationService.getUserChatHistory({
            Session_Id: this.chatData.session_id,
            MobileNumber: this.chatData.patient_mobile || ''
          }).pipe(
            catchError((err) => {
              console.log('Polling Error - Full object:', JSON.stringify(err, null, 2));
              console.log('Polling Error - status:', err.status);
              console.log('Polling Error - message:', err.message);
              console.log('Polling Error - error:', err.error);
              
              // Handle session expired error - handle multiple possible response formats
              const statusCode = err.status;
              const errorMessage = (err.error && (err.error.message || err.error.Message)) || err.message || '';
              const statusCodeInError = err.error && err.error.statusCode;
              
              console.log('Polling - Checking for session error - statusCode:', statusCode, 'errorMessage:', errorMessage, 'statusCodeInError:', statusCodeInError);
              
              if ((statusCode === 400 || statusCodeInError === 400) && 
                  (errorMessage.includes('Invalid session_id') || errorMessage.includes('invalid session_id'))) {
                console.log('Polling - Session expired error detected!');
                this.messageService.showErrorMessage('Session expired');
                if (this.pollSubscription) {
                  this.pollSubscription.unsubscribe();
                }
              }
              return of(null);
            })
          )
        )
      )
      .subscribe((res) => {
        if (res && res.messages) {
          const prevCount = this.messages.length;
          this.messages = res.messages;
          this.rebuildTimeline();
          if (res.messages.length > prevCount) {
            this.shouldScroll = true;
          }
          // Update read-only state if backend reports agent has exited
          if (res.isAgentExit === 1) {
            this.isReadOnly = true;
          }
        }
      });
  }

  private scrollToBottom(): void {
    try {
      if (this.chatBodyRef) {
        this.chatBodyRef.nativeElement.scrollTop = this.chatBodyRef.nativeElement.scrollHeight;
      }
    } catch (e) {}
  }

  isUserMessage(msg: UserChatHistoryDto): boolean {
    return (msg.role || '').toLowerCase() === 'user';
  }

  private rebuildTimeline(): void {
    const dbMessages: TimelineMessage[] = (this.messages || []).map((msg) => {
      const date = this.parseBackendDateAsUtc(msg.created_on);
      return {
        role: (msg.role || 'BOT').toUpperCase(),
        message_text: msg.message_text,
        options: (msg.options || []).map((opt) => ({ label: opt.label })),
        created_on_date: date,
        isUser: this.isUserMessage(msg)
      };
    });

    const localAgentMessages: TimelineMessage[] = (this.sentMessages || [])
      .filter((sent) => !this.hasMatchingDbAgentMessage(sent, dbMessages))
      .map((sent) => ({
        role: 'AGENT',
        message_text: sent.text,
        options: [],
        created_on_date: sent.time,
        isUser: false
      }));

    this.timelineMessages = [...dbMessages, ...localAgentMessages]
      .sort((a, b) => a.created_on_date.getTime() - b.created_on_date.getTime());
  }

  // Backend timestamps come from MySQL without timezone, treat as UTC and convert for display.
  private parseBackendDateAsUtc(value: string): Date {
    if (!value) {
      return new Date(0);
    }

    const raw = String(value).trim();
    if (!raw) {
      return new Date(0);
    }

    if (/[zZ]$|[+\-]\d{2}:?\d{2}$/.test(raw)) {
      const parsed = new Date(raw);
      return isNaN(parsed.getTime()) ? new Date(0) : parsed;
    }

    const normalized = raw.replace(' ', 'T');
    const parsed = new Date(normalized + 'Z');
    return isNaN(parsed.getTime()) ? new Date(0) : parsed;
  }

  private hasMatchingDbAgentMessage(sent: SentMessage, dbMessages: TimelineMessage[]): boolean {
    const sentMs = sent.time.getTime();
    const sentText = this.normalizeMessageText(sent.text);
    return dbMessages.some((msg) => {
      // Backend can persist agent replies as BOT/AGENT depending on flow.
      if (msg.isUser) {
        return false;
      }
      if (this.normalizeMessageText(msg.message_text) !== sentText) {
        return false;
      }
      const diff = Math.abs(msg.created_on_date.getTime() - sentMs);
      return diff <= 2 * 60 * 1000;
    });
  }

  private normalizeMessageText(value: string): string {
    return (value || '').replace(/\s+/g, ' ').trim().toLowerCase();
  }

  sendMessage(): void {
    const text = this.newMessage.trim();
    if (!text || this.isSending) { return; }

    this.isSending = true;
    this.agentLeadNotificationService.messageReceived({
      Phone_number: this.chatData.patient_mobile,
      Session_id: this.chatData.session_id,
      Agent_id: this.agentId,
      Message: text
    }).subscribe(
      () => {
        this.sentMessages.push({ text, time: new Date() });
        this.rebuildTimeline();
        this.newMessage = '';
        this.isSending = false;
        this.shouldScroll = true;
      },
      (err) => {
        this.isSending = false;
        console.log('Full Error object:', JSON.stringify(err, null, 2));
        console.log('Error status:', err.status);
        console.log('Error message:', err.message);
        console.log('Error error:', err.error);
        
        // Check for session expired error - handle multiple possible response formats
        const statusCode = err.status;
        const errorMessage = (err.error && (err.error.message || err.error.Message)) || err.message || '';
        const statusCodeInError = err.error && err.error.statusCode;
        
        console.log('Checking for session error - statusCode:', statusCode, 'errorMessage:', errorMessage, 'statusCodeInError:', statusCodeInError);
        
        if ((statusCode === 400 || statusCodeInError === 400) && 
            (errorMessage.includes('Invalid session_id') || errorMessage.includes('invalid session_id'))) {
          console.log('Session expired error detected!');
          this.messageService.showErrorMessage('Session expired');
        }
      }
    );
  }

  exitChat(): void {
    if (this.isExiting) { return; }
    this.isExiting = true;
    this.agentLeadNotificationService.exitAgent({
      Phone_number: this.chatData.patient_mobile,
      Session_id: this.chatData.session_id,
      Agent_id: this.agentId
    }).subscribe(
      () => this.close.emit(),
      (_err) => {
        this.isExiting = false;
        this.close.emit();
      }
    );
  }

  onClose(): void {
    this.close.emit();
  }
}
